import type { ColumnDef, Row } from "@tanstack/react-table";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type {
  FormOption,
  ScriptLookupType,
  SensorSelectType,
} from "~/types/ui";
import DataTable from "../../common/table";
import { SearchHeader } from "../../common/table-components";
import { ContentContainer } from "../../common/content-container";
import type { NodeDto, TagKeyDto } from "~/types/api";

export function TagLookupTable({ options }: { options?: ScriptLookupType[] }) {
  const [search, setSearch] = useState("");
  const { t } = useTranslation();

  const colDefs: ColumnDef<any>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: ({ column }) => (
        <SearchHeader column={column} child="nodes.headers.name" />
      ),
      cell: (info) => info.getValue(),
      footer: (props) => props.column.id,
    },
    {
      id: "deveui",
      accessorKey: "deveui",
      header: ({ column }) => (
        <SearchHeader column={column} child="nodes.headers.deveui" />
      ),
      cell: (info) => info.getValue(),
      footer: (props) => props.column.id,
    },
  ];

  const data =
    getTagsFromOptions(options).map((x) => {
      return {
        name: x.label,
        deveui: x.value,
        sensors: [] as TagKeyDto[],
      };
    }) ?? [];

  for (let sensors of getSensorsFromOptions(options)) {
    const entry = data.find((x) => x.deveui == sensors.deveui);
    if (entry) {
      entry.sensors.push({
        key: sensors.tagKey.value,
        label: sensors.tagKey.label
          ? sensors.tagKey.label
          : sensors.tagKey.value,
      });
    }
  }

  return (
    <ContentContainer>
      <div className="text-muted-foreground text-sm mb-1">
        {t("scripts.lookupTable.tag.expand")}
      </div>
      <DataTable
        data={data}
        columns={colDefs}
        searchbar
        searchValue={search}
        setSearchValue={setSearch}
        expandable
        expandableChild={(row: Row<any>) => (
          <TagLookupTableExpandable row={row} />
        )}
      />
    </ContentContainer>
  );
}

function getTagsFromOptions(
  options?: ScriptLookupType[],
): FormOption<string>[] {
  return (options?.filter((x) => x.label == "tag")?.at(0)?.value ??
    []) as FormOption<string>[];
}

function getSensorsFromOptions(options?: ScriptLookupType[]) {
  return (
    (
      options?.filter((x) => x.label == "sensor")?.at(0)
        ?.value as SensorSelectType
    )?.sensors ?? []
  );
}

export function TagLookupTableExpandable({
  row,
}: {
  row: Row<any>;
}): ReactNode {
  const { t } = useTranslation();
  const node = row.original as NodeDto;
  return (
    <div className="p-4">
      <TagLookupTableSensors sensors={node.sensors} />
    </div>
  );
}

export function TagLookupTableSensors({
  sensors,
}: {
  sensors: TagKeyDto[];
}): ReactNode {
  const colDefs: ColumnDef<any>[] = [
    {
      accessorKey: "label",
      header: (info) => (
        <SearchHeader
          child="nodes.currents.headers.label"
          column={info.column}
        />
      ),
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "key",
      cell: (info) => info.getValue(),
      header: (info) => (
        <SearchHeader child="nodes.currents.headers.key" column={info.column} />
      ),
    },
  ];

  return <DataTable columns={colDefs} data={sensors} />;
}
